<template>
  <div
    class="job-architecture-treeview-item d-flex flex-column align-start justify-start"
    :class="{
      'job-architecture-treeview-item-selected': isSelected,
      'job-architecture-treeview-item-group': depth === 0 && !isLastGroup,
    }"
  >
    <div
      class="job-architecture-treeview-item-row architecture-row-grid"
      @click="handleRowClick"
    >
      <div
        class="job-architecture-treeview-col-name d-flex align-center"
        :class="leftPaddingClass"
      >
        <v-icon
          v-if="hasChildren"
          :icon="isOpen ? '$chevron-down' : '$chevron-right'"
          color="iconDark"
          :size="20"
          class="job-architecture-treeview-item-expand-icon"
          @click.stop="isOpen = !isOpen"
        />
        <div
          class="job-architecture-treeview-item-content"
          :class="contentOffsetClass"
        >
          <div
            v-if="isChild"
            class="job-architecture-treeview-item-checkbox-wrap"
            @click.stop
          >
            <DpplSimpleCheckbox
              :model-value="isSelected"
              :option="{ id: String(nodeValue), label: '' }"
              hide-label
              :disabled="isEmptyJobFamily"
              @update:model-value="toggleSelection"
            />
          </div>
          <span
            class="job-architecture-treeview-item-label"
            :class="labelTypographyClass"
          >
            <span class="job-architecture-treeview-item-title-row d-flex align-end flex-wrap ga-1">
              <span
                class="job-architecture-treeview-item-title-text"
                :class="{ 'ml-1': !isChild }"
              >
                <slot
                  name="item-label"
                  :item="item"
                >
                  {{ item[itemTitle] }}
                </slot>
              </span>
              <span
                v-if="item.jobCode"
                class="job-architecture-job-code text-body-3 ml-2 flex-shrink-0"
              >
                {{ item.jobCode }}
              </span>
              <span
                v-if="item.jobLevel"
                class="job-architecture-job-level-badge text-body-3 ml-2 text-textLight px-2"
              >
                {{ item.jobLevel }}
              </span>
            </span>
          </span>
        </div>
      </div>
      <div class="job-architecture-treeview-col-families text-body-1 w-100">
        {{ item.structureFamiliesLabel || '' }}
      </div>
      <div class="job-architecture-treeview-col-profiles text-body-1 w-100">
        {{ item.structureProfilesLabel || '' }}
      </div>
      <div class="job-architecture-treeview-col-employees text-body-1 w-100">
        {{ item.employeesDisplay ?? '' }}
      </div>
      <div class="w-100" />
    </div>
    <JobArchitectureTreeviewItemChildren
      v-if="hasChildren && isOpen"
      :items="item[itemChildren]"
      :depth="depth + 1"
      :item-value="itemValue"
      :item-title="itemTitle"
      :item-children="itemChildren"
      :selected="selected"
      :selectable="selectable"
      :level-has-children="anyHaveChildren"
      @update:selected="$emit('update:selected', $event)"
    >
      <template #item-label="{ item: slotItem }">
        <slot
          name="item-label"
          :item="slotItem"
        />
      </template>
    </JobArchitectureTreeviewItemChildren>
  </div>
</template>

<script setup>
import { computed, ref, defineAsyncComponent } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter, useRoute } from 'vue-router';
import DpplSimpleCheckbox from '@/components/datapeople/DpplSimpleCheckbox.vue';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';

const JobArchitectureTreeviewItemChildren = defineAsyncComponent(
  () => import('@/components/shared/JobArchitectureTreeviewItemChildren.vue'),
);

const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
  depth: {
    type: Number,
    default: 0,
  },
  itemValue: {
    type: String,
    default: 'value',
  },
  itemTitle: {
    type: String,
    default: 'title',
  },
  itemChildren: {
    type: String,
    default: 'children',
  },
  selected: {
    type: Array,
    default: () => [],
  },
  selectable: {
    type: Boolean,
    default: true,
  },
  isChild: {
    type: Boolean,
    default: false,
  },
  levelHasChildren: {
    type: Boolean,
    default: false,
  },
  isLastGroup: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:selected']);

const router = useRouter();
const route = useRoute();

const { listDefaultExpandedNodeIds } = storeToRefs(useJobArchitecture());

const hasChildNodes = (item, childrenKey) => {
  const children = item[childrenKey];

  return Array.isArray(children) && children.length > 0;
};

const getLeafValues = (item, itemValueKey, itemChildrenKey) => {
  if (!hasChildNodes(item, itemChildrenKey)) return [item[itemValueKey]];

  return item[itemChildrenKey].flatMap((child) => getLeafValues(child, itemValueKey, itemChildrenKey));
};

// If the node has children and is in the default expanded node ids, open it by default.
const initialIsOpen = hasChildNodes(props.item, props.itemChildren)
  && Boolean(listDefaultExpandedNodeIds.value?.has(props.item[props.itemValue]));

const isOpen = ref(initialIsOpen);

const hasChildren = computed(() => hasChildNodes(props.item, props.itemChildren));

const nodeValue = computed(() => props.item[props.itemValue]);

const leafValuesForNode = computed(() => getLeafValues(props.item, props.itemValue, props.itemChildren));

const selectedLeafCount = computed(() => leafValuesForNode.value
  .filter((value) => props.selected.includes(value)).length);

const isSelected = computed(() => {
  if (!hasChildren.value) return props.selected.includes(nodeValue.value);

  return selectedLeafCount.value === leafValuesForNode.value.length;
});

const anyHaveChildren = computed(() => (props.item[props.itemChildren] || [])
  .some((child) => hasChildNodes(child, props.itemChildren)));

const leftPaddingClass = computed(() => {
  if (!props.isChild && props.levelHasChildren) return 'pl-1';
  if (!props.isChild && !props.levelHasChildren) return 'pl-0';
  if (hasChildren.value) return 'pl-4';
  if (props.levelHasChildren) return 'pl-4';

  return 'pl-12';
});

const contentOffsetClass = computed(() => {
  if (props.levelHasChildren && !hasChildren.value) return 'ml-5';

  return '';
});

const isEmptyJobFamily = computed(() => props.depth === 1 && !hasChildren.value);

const labelTypographyClass = computed(() => {
  if (props.depth === 0) return 'text-h2';
  if (props.depth === 1) return 'text-h3';

  return 'text-body-1';
});

const toggleSelection = () => {
  let updatedSelected;

  if (hasChildren.value) {
    const leafValues = leafValuesForNode.value;
    const isSelecting = !isSelected.value;

    if (isSelecting) {
      isOpen.value = true;

      updatedSelected = [...new Set([...props.selected, ...leafValues])];
    } else {
      const leafSet = new Set(leafValues);

      updatedSelected = props.selected.filter((v) => !leafSet.has(v));
    }
  } else {
    updatedSelected = isSelected.value
      ? props.selected.filter((v) => v !== nodeValue.value)
      : [...props.selected, nodeValue.value];
  }

  emit('update:selected', updatedSelected);
};

const handleRowClick = (event) => {
  if (event.target.closest('.job-architecture-treeview-item-expand-icon')) return;
  if (event.target.closest('.job-architecture-treeview-item-checkbox-wrap')) return;

  if (props.depth === 2) {
    router.push({ query: { ...route.query, preview: nodeValue.value, type: 'jobProfileHris' } });
  } else {
    isOpen.value = !isOpen.value;
  }
};
</script>

<style scoped lang="less">
@import '@/less/mixins';

.architecture-row-grid {
  width: 100%;
  min-height: 48px;
  box-sizing: border-box;

  &:hover {
    background: rgba(@bg-brand-default, 0.05);
  }
}

.job-architecture-treeview-item-group {
  border-bottom: 1px solid @stroke-default;
}

.job-architecture-treeview-item {
  width: 100%;
  color: @text-darkest;

  & > .architecture-row-grid {
    display: grid;
    align-items: center;
    grid-template-columns: minmax(0, 1fr) minmax(70px, 105px) minmax(80px, 120px) 88px minmax(160px, 185px);
    column-gap: 12px;
    padding: 4px 16px;
    width: 100%;
  }
}

.job-architecture-treeview-item-selected .job-architecture-treeview-item-row {
  background: @grey-100;
}

.job-architecture-treeview-col-name {
  justify-content: flex-start;
}

.job-architecture-treeview-col-families {
  color: @text-dark;
}

.job-architecture-treeview-col-profiles {
  color: @brand-primary;
  font-weight: 500;
}

.job-architecture-treeview-item-content {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 8px;
}

.job-architecture-treeview-item-title-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-architecture-treeview-item-label {
  overflow: visible;
  cursor: pointer;
  flex: 1;
}

.job-architecture-job-level-badge {
  border-radius: 100px;
  border: 1px solid @stroke-default;
}

.job-architecture-job-code {
  color: @text-lighter;
}

.job-architecture-treeview-item-checkbox-wrap {
  margin: 0;
  padding: 0 4px 0 0;
}

.job-architecture-treeview-item-expand-icon {
  cursor: pointer;

  &:focus,
  &:focus-visible {
    outline: none !important;
  }
}

:deep(input[type=checkbox]) {
  min-width: 15px;
  min-height: 15px;
  width: 15px;
  height: 15px;

  &::before {
    left: 4px;
    width: 5px;
    height: 9px;
  }
}
</style>
